import React from 'react';
import { useApolloClient } from '@apollo/react-hooks';
import { useRetailStoresDetailsContext } from '../../../../platform/ux/RetailStores/context/RetailStoresContext';
import { GoogleApiWrapper } from 'google-maps-react';
import googleMapsConfig from '../../../../platform/shared/maps/googlemaps';
import { message } from 'antd';
import { parseOverlayToGeoTarget } from '../../../utils/geoTargets';
import OverlaySpinner from '../../../../platform/shared/OverlaySpinner';
import moment from 'moment';

const RetailStoresDetailsController = (props) => {
  const {
    locations,
    updateLocation,
    updateLocationWithGeotargets,
    updateLocationWithLicense,
    createGeoTargetLocation,
    refetch,
    currentUser,
    loading,
    permission,
    userLoading,
  } = useRetailStoresDetailsContext();

  const client = useApolloClient();

  if (loading || !locations || !currentUser || !permission || userLoading) {
    return <OverlaySpinner />;
  }

  const geoframe = locations.locations[0].geoframe;
  let shapes = [];
  shapes.push(geoframe);

  const onUpdateRetailLocation = async (data) => {
    const { role, id: userId } = currentUser;

    const inProgressOverlay = data.inProgressOverlay;

    let circles = [],
      polygons = [];

    if (inProgressOverlay && inProgressOverlay.length > 0) {
      const parsedData = parseOverlayToGeoTarget(inProgressOverlay);
      circles = parsedData.circles;
      polygons = parsedData.polygons;
    }

    try {
      message.loading('Updating location...', 0);

      await client.clearStore();

      let geoId = '';

      await createGeoTargetLocation({
        variables: {
          name: data.locationName,
          orgID: role.org.id,
          isBase: true,
          circles: circles,
          polygons: polygons,
          start: moment().subtract(90, 'days').format(),
          end: moment().format(),
        },
      })
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.createGeoTarget &&
            response.data.createGeoTarget.id
          ) {
            geoId = response.data.createGeoTarget.id;
          }
        })
        .catch((error) => {
          console.error('Failed to create GeoTarget:', error);
        });

      // Convert designation value to array for mutation
      const getDesignationsArray = (designation) => {
        if (designation === 'BOTH') return ['MEDICAL', 'RECREATIONAL'];
        if (designation === 'MEDICAL') return ['MEDICAL'];
        if (designation === 'RECREATIONAL') return ['RECREATIONAL'];
        return [];
      };

      const designations = getDesignationsArray(data.locationDesignation);

      // Base variables for all mutations
      const baseVariables = {
        id: locations.locations[0].id,
        name: data.locationName,
        description: data.locationDescription,
        street: data.locationAddress,
        city: data.locationCity,
        state: data.locationState,
        country: data.locationCountry,
        email: data.email || '',
        url: data.url || '',
        phone: data.phone || '',
        contact: data.contact || '',
        formattedAddress: data.formattedAddress,
        zip: data.locationZip,
        locationKey: data.locationKey,
        lat: data.lat,
        lng: data.lng,
        audienceType: data.locationAudienceType,
      };

      // Additional variables for original mutations
      const originalMutationVars = {
        ...baseVariables,
        cart: data.locationCart,
        menuDataKey: data.menuDataKey || null,
        updatedBy: userId,
      };

      // Variables for license mutation
      const licenseMutationVars = {
        ...baseVariables,
        licenseId: data.licenseId || null,
        designations: designations.length > 0 ? designations : null,
        licenseIdentifier: data.locationLicenseIdentifier || null,
      };

      const variables = originalMutationVars;

      // Handle state/province mapping based on country
      if (variables.country === 'USA') {
        // For USA: keep state, explicitly null province
        variables.province = null;
      } else if (variables.country === 'CANADA') {
        // For Canada: move state to province, clear state
        variables.province = variables.state;
        variables.state = null;
      } else {
        // For other countries: clear state, explicitly set province (even if null)
        variables.state = null;
        // Ensure province is always defined (null if no province data)
        variables.province = variables.province || null;
      }

      // Determine which mutations to call
      const hasGeotargets = circles.length !== 0 || polygons.length !== 0;
      const hasLicenseChanges = designations.length > 0 || data.licenseId || data.locationLicenseIdentifier;

      let result;
      licenseMutationVars.province = variables.province;
      licenseMutationVars.state = variables.state;

      if (hasGeotargets) {
        // Has geotargets - use updateLocationWithGeotargets
        result = await updateLocationWithGeotargets({ variables: { ...variables, geoId } });
        // Only update license if there are license changes
        if (hasLicenseChanges) {
          await updateLocationWithLicense({ variables: licenseMutationVars });
        }
      } else {
        // No geotargets - use updateLocationWithLicense only
        result = await updateLocationWithLicense({ variables: licenseMutationVars });
      }

      if (result.data) {
        message.destroy();
        message.success('Location updated successfully');
        window.location.reload();
      }
    } catch (err) {
      message.destroy();
      message.error('Error updating location:', err);
      throw err;
    }
  };

  return React.cloneElement(props.children, {
    ...props.children.props,
    onUpdateRetailLocation,
    google: props.google,
    shapes,
  });
};

export default GoogleApiWrapper(googleMapsConfig)(RetailStoresDetailsController);
